import React from "react";
import { fireEvent, render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";
import { FullPageChatLayout } from "./FullPageChatLayout";

jest.mock("@metalmind-ab/mmagent-chat-ui", () => ({
  ChatMessage: () => null,
  JobProgressIndicator: () => null,
  AttachmentPicker: ({
    attachments,
    onChange,
    fileHandlers,
    uploadImage,
    refusal,
  }: {
    attachments: { fileName: string }[];
    onChange: (attachments: unknown[]) => void;
    fileHandlers?: Record<string, (file: File) => Promise<unknown>>;
    uploadImage?: (file: File) => Promise<unknown>;
    refusal?: string;
  }) => (
    <div data-testid="attachment-picker">
      <button
        type="button"
        onClick={() =>
          onChange([
            {
              kind: "image",
              reference: {
                attachment_id: "image-1",
                media_type: "image/png",
                size_bytes: 4,
              },
              fileName: "photo.png",
              previewUrl: "blob:photo",
            },
          ])
        }
      >
        Select image
      </button>
      {[
        "report.pdf",
        "diagram.gif",
        "drawing.svg",
        "photo.bmp",
        "scan.tiff",
      ].map((name) => (
        <button
          key={name}
          type="button"
          onClick={async () => {
            const file = new File(["document"], name, {
              type: name.endsWith(".pdf")
                ? "application/pdf"
                : `image/${name.split(".").pop()}`,
            });
            const handler = fileHandlers?.[name.slice(name.lastIndexOf("."))];
            if (!handler) return;
            const reference = await handler(file);
            onChange([
              ...attachments,
              {
                kind: "document",
                reference,
                fileName: file.name,
                size: file.size,
                mediaType: file.type,
              },
            ]);
          }}
        >
          Select {name}
        </button>
      ))}
      <button
        type="button"
        onClick={async () => {
          const file = new File(["image"], "photo.png", { type: "image/png" });
          const handler = fileHandlers?.[".png"];
          if (!handler) return;
          const reference = await handler(file);
          onChange([
            ...attachments,
            {
              kind: "document",
              reference,
              fileName: file.name,
              size: file.size,
              mediaType: file.type,
            },
          ]);
        }}
      >
        Select photo.png as document
      </button>
      <span data-testid="picker-handlers">
        {Object.keys(fileHandlers || {}).join(",")}
      </span>
      <span data-testid="picker-image-upload">
        {String(Boolean(uploadImage))}
      </span>
      {attachments.map((attachment) => (
        <span key={attachment.fileName}>{attachment.fileName}</span>
      ))}
      {refusal && <span data-testid="attachment-refusal">{refusal}</span>}
    </div>
  ),
  useChatStore: (selector: (state: object) => unknown) =>
    selector({
      backgroundJobs: [],
      removeBackgroundJob: jest.fn(),
      messages: [],
    }),
}));

beforeAll(() => {
  Object.defineProperty(HTMLElement.prototype, "scrollIntoView", {
    configurable: true,
    value: jest.fn(),
  });
  Object.defineProperty(URL, "revokeObjectURL", {
    configurable: true,
    value: jest.fn(),
  });
  Object.defineProperty(URL, "createObjectURL", {
    configurable: true,
    value: jest.fn(() => "blob:photo"),
  });
});

function renderLayout(
  overrides: Partial<React.ComponentProps<typeof FullPageChatLayout>> = {},
) {
  return render(
    <FullPageChatLayout
      messages={[]}
      onSendMessage={jest.fn()}
      isLoading={false}
      conversations={[]}
      onLoadConversation={jest.fn()}
      onNewConversation={jest.fn()}
      onSearchConversations={jest.fn()}
      onLoadMoreConversations={jest.fn()}
      conversationsLoading={false}
      currentConversationId={1}
      {...overrides}
    />,
  );
}

function composerTextArea(): HTMLTextAreaElement {
  return screen.getByRole("textbox") as HTMLTextAreaElement;
}

const documentExtensions = [
  ".pdf",
  ".docx",
  ".doc",
  ".xlsx",
  ".xls",
  ".pptx",
  ".ppt",
  ".txt",
  ".md",
  ".csv",
  ".json",
  ".html",
  ".gif",
  ".bmp",
  ".svg",
  ".tiff",
  ".tif",
];
const imageExtensions = [".png", ".jpg", ".jpeg", ".webp"];

function pickerHandlers(): string[] {
  return screen.getByTestId("picker-handlers").textContent?.split(",") || [];
}

it("keeps text-only sends on the existing one-argument path", () => {
  const onSendMessage = jest.fn();
  renderLayout({ onSendMessage });
  fireEvent.change(composerTextArea(), { target: { value: "hello" } });
  fireEvent.click(screen.getByRole("button", { name: "Send message" }));
  expect(onSendMessage).toHaveBeenCalledWith("hello");
});

it("keeps documents available when image capability is unavailable", async () => {
  const onSendMessageWithFiles = jest.fn().mockResolvedValue(undefined);
  renderLayout({ imageAttachmentsEnabled: false, onSendMessageWithFiles });
  expect(screen.getByTestId("attachment-picker")).toBeInTheDocument();
  expect(screen.getByTestId("picker-image-upload")).toHaveTextContent("false");
  expect(pickerHandlers()).toEqual(
    expect.arrayContaining([...documentExtensions, ...imageExtensions]),
  );
  expect(pickerHandlers()).toHaveLength(21);
  fireEvent.click(
    screen.getByRole("button", { name: "Select photo.png as document" }),
  );
  await screen.findByText("photo.png");
  fireEvent.change(composerTextArea(), { target: { value: "read this" } });
  fireEvent.click(screen.getByRole("button", { name: "Send message" }));
  expect(onSendMessageWithFiles).toHaveBeenCalledWith("read this", [
    expect.objectContaining({ name: "photo.png" }),
  ]);
});

it("routes image formats as documents when uploadImage is omitted", async () => {
  const onSendMessageWithFiles = jest.fn().mockResolvedValue(undefined);
  renderLayout({ imageAttachmentsEnabled: true, onSendMessageWithFiles });
  expect(pickerHandlers()).toEqual(
    expect.arrayContaining([...documentExtensions, ...imageExtensions]),
  );
  expect(pickerHandlers()).toHaveLength(21);
  fireEvent.click(
    screen.getByRole("button", { name: "Select photo.png as document" }),
  );
  await screen.findByText("photo.png");
  fireEvent.change(composerTextArea(), { target: { value: "read this" } });
  fireEvent.click(screen.getByRole("button", { name: "Send message" }));
  expect(onSendMessageWithFiles).toHaveBeenCalledWith("read this", [
    expect.objectContaining({ name: "photo.png" }),
  ]);
});

it("keeps text and image selection editable after a send refusal", async () => {
  const onSendMessage = jest
    .fn()
    .mockRejectedValue(new Error("IMAGE_INPUT_UNSUPPORTED"));
  renderLayout({
    imageAttachmentsEnabled: true,
    uploadImage: jest.fn(),
    onSendMessage,
  });
  fireEvent.change(composerTextArea(), { target: { value: "describe this" } });
  fireEvent.click(screen.getByRole("button", { name: "Select image" }));
  fireEvent.click(screen.getByRole("button", { name: "Send message" }));
  expect(
    await screen.findByText("IMAGE_INPUT_UNSUPPORTED"),
  ).toBeInTheDocument();
  expect(composerTextArea()).toHaveValue("describe this");
  expect(screen.getByText("photo.png")).toBeInTheDocument();
  expect(screen.getByTestId("attachment-refusal")).toHaveTextContent(
    "IMAGE_INPUT_UNSUPPORTED",
  );
});

it("sends selected documents through IDA's existing file path", async () => {
  const onSendMessageWithFiles = jest.fn().mockResolvedValue(undefined);
  renderLayout({
    imageAttachmentsEnabled: true,
    uploadImage: jest.fn(),
    onSendMessageWithFiles,
  });
  expect(screen.getAllByTestId("attachment-picker")).toHaveLength(1);
  expect(pickerHandlers()).toEqual(expect.arrayContaining(documentExtensions));
  expect(pickerHandlers()).toHaveLength(17);
  expect(
    screen.queryByLabelText("Choose document attachments"),
  ).not.toBeInTheDocument();
  fireEvent.click(screen.getByRole("button", { name: "Select report.pdf" }));
  await screen.findByText("report.pdf");
  fireEvent.change(composerTextArea(), { target: { value: "read this" } });
  fireEvent.click(screen.getByRole("button", { name: "Send message" }));
  expect(onSendMessageWithFiles).toHaveBeenCalledWith("read this", [
    expect.objectContaining({ name: "report.pdf" }),
  ]);
});

it("restores the text and document attachment when the send request fails", async () => {
  const onSendMessageWithFiles = jest
    .fn()
    .mockRejectedValue(new Error("Service temporarily unavailable"));
  renderLayout({ onSendMessageWithFiles });
  fireEvent.click(screen.getByRole("button", { name: "Select report.pdf" }));
  await screen.findByText("report.pdf");
  fireEvent.change(composerTextArea(), { target: { value: "read this" } });

  fireEvent.click(screen.getByRole("button", { name: "Send message" }));

  expect(
    await screen.findByText("Service temporarily unavailable"),
  ).toBeInTheDocument();
  expect(composerTextArea()).toHaveValue("read this");
  expect(screen.getByText("report.pdf")).toBeInTheDocument();
});

it("clears the text and document attachment after a successful send", async () => {
  const onSendMessageWithFiles = jest.fn().mockResolvedValue(undefined);
  renderLayout({ onSendMessageWithFiles });
  fireEvent.click(screen.getByRole("button", { name: "Select report.pdf" }));
  await screen.findByText("report.pdf");
  fireEvent.change(composerTextArea(), { target: { value: "read this" } });

  fireEvent.click(screen.getByRole("button", { name: "Send message" }));

  expect(onSendMessageWithFiles).toHaveBeenCalledWith("read this", [
    expect.objectContaining({ name: "report.pdf" }),
  ]);
  expect(composerTextArea()).toHaveValue("");
  expect(screen.queryByText("report.pdf")).not.toBeInTheDocument();
});

it.each(["diagram.gif", "drawing.svg", "photo.bmp", "scan.tiff"])(
  "routes %s through IDA's document upload handler",
  async (name) => {
    const onSendMessageWithFiles = jest.fn().mockResolvedValue(undefined);
    renderLayout({
      imageAttachmentsEnabled: true,
      uploadImage: jest.fn(),
      onSendMessageWithFiles,
    });
    expect(screen.getByTestId("picker-handlers")).toHaveTextContent(
      name.slice(name.lastIndexOf(".")),
    );
    fireEvent.click(screen.getByRole("button", { name: `Select ${name}` }));
    await screen.findByText(name);
    fireEvent.change(composerTextArea(), { target: { value: "read this" } });
    fireEvent.click(screen.getByRole("button", { name: "Send message" }));
    expect(onSendMessageWithFiles).toHaveBeenCalledWith("read this", [
      expect.objectContaining({ name }),
    ]);
  },
);

it("sends a selected image on the chat-image path", async () => {
  const onSendMessage = jest.fn();
  const onSendMessageWithFiles = jest.fn();
  renderLayout({
    imageAttachmentsEnabled: true,
    uploadImage: jest.fn(),
    onSendMessage,
    onSendMessageWithFiles,
  });
  fireEvent.click(screen.getByRole("button", { name: "Select image" }));
  fireEvent.change(composerTextArea(), { target: { value: "describe this" } });
  fireEvent.click(screen.getByRole("button", { name: "Send message" }));
  expect(onSendMessage).toHaveBeenCalledWith(
    "describe this",
    [{ attachment_id: "image-1", media_type: "image/png", size_bytes: 4 }],
    expect.any(Function),
  );
  expect(onSendMessageWithFiles).not.toHaveBeenCalled();
});

it("leaves an accepted image preview for the sent message", async () => {
  const onSendMessage = jest.fn(async (...args: unknown[]) => {
    (args[2] as (() => void) | undefined)?.();
  });
  renderLayout({
    imageAttachmentsEnabled: true,
    uploadImage: jest.fn(),
    onSendMessage,
  });
  fireEvent.click(screen.getByRole("button", { name: "Select image" }));
  fireEvent.click(screen.getByRole("button", { name: "Send message" }));
  expect(onSendMessage).toHaveBeenCalled();
  expect(screen.queryByText("photo.png")).not.toBeInTheDocument();
  expect(URL.revokeObjectURL).not.toHaveBeenCalledWith("blob:photo");
});

it("clears unsent attachments when the active conversation changes", () => {
  const { rerender } = renderLayout({
    currentConversationId: 1,
    imageAttachmentsEnabled: true,
    onSendMessageWithFiles: jest.fn(),
  });
  fireEvent.click(screen.getByRole("button", { name: "Select image" }));
  expect(screen.getByText("photo.png")).toBeInTheDocument();

  rerender(
    <FullPageChatLayout
      messages={[]}
      onSendMessage={jest.fn()}
      isLoading={false}
      conversations={[]}
      onLoadConversation={jest.fn()}
      onNewConversation={jest.fn()}
      onSearchConversations={jest.fn()}
      onLoadMoreConversations={jest.fn()}
      conversationsLoading={false}
      currentConversationId={2}
      imageAttachmentsEnabled
      onSendMessageWithFiles={jest.fn()}
    />,
  );

  expect(screen.queryByText("photo.png")).not.toBeInTheDocument();
  expect(URL.revokeObjectURL).not.toHaveBeenCalled();
});

it("sends a selected image and document in one message", async () => {
  const onSendMessage = jest.fn();
  const onSendMessageWithFiles = jest.fn().mockResolvedValue(undefined);
  renderLayout({
    imageAttachmentsEnabled: true,
    uploadImage: jest.fn(),
    onSendMessage,
    onSendMessageWithFiles,
  });
  fireEvent.click(screen.getByRole("button", { name: "Select image" }));
  fireEvent.click(screen.getByRole("button", { name: "Select report.pdf" }));
  await screen.findByText("report.pdf");
  fireEvent.change(composerTextArea(), { target: { value: "compare" } });
  fireEvent.click(screen.getByRole("button", { name: "Send message" }));
  expect(onSendMessageWithFiles).toHaveBeenCalledWith(
    "compare",
    [expect.objectContaining({ name: "report.pdf" })],
    [{ attachment_id: "image-1", media_type: "image/png", size_bytes: 4 }],
  );
  expect(onSendMessage).not.toHaveBeenCalled();
  expect(
    screen.queryByText("Send images and documents in separate messages."),
  ).not.toBeInTheDocument();
  expect(URL.revokeObjectURL).not.toHaveBeenCalledWith("blob:photo");
});
